import axios, { AxiosError } from 'axios';
import { ActivityAction, ActivityResourceName } from '../interfaces/activities';
import { CommonRootState } from '../combineReducers';
import { DeepReadonly } from 'utility-types';
import { IUserInfo, IUserInfoScope, UserInfoActivitiesType } from '../interfaces/userInfo';
import { sendError } from '../sendError';
import { ThunkAction } from 'redux-thunk';

const { Global } = UserInfoActivitiesType;

export const fetchUserInfoRequest = () =>
  ({
    type: 'FETCH_USERINFO_REQUEST'
  } as const);

export const hasPermission = (
  userInfo: DeepReadonly<IUserInfo> | null,
  action: ActivityAction,
  resourceName: ActivityResourceName,
  additionalScopes: IUserInfoScope[] = []
) =>
  userInfo
    ? [
        {
          id: null,
          type: Global
        } as IUserInfoScope
      ]
        .concat(additionalScopes)
        .some(({ id: findId, type: findType }) => {
          const foundScope = userInfo.activities.find(({ id, type }) => id === findId && type === findType);
          return (
            foundScope !== undefined &&
            foundScope.activities[resourceName] !== undefined &&
            foundScope.activities[resourceName]!.includes(action)
          );
        })
    : false;

export const fetchUserInfoSuccess = (payload: IUserInfo) =>
  ({
    type: 'FETCH_USERINFO_SUCCESS',
    payload
  } as const);

export const fetchUserInfoFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: 'FETCH_USERINFO_FAILURE',
    error
  } as const;
};

const shouldFetchUserInfo = (state: CommonRootState) => {
  const { isFetching, error, userInfo } = state.userInfo;

  return !isFetching && !error && !userInfo;
};

const fetchUserInfo = (): ThunkAction<Promise<IUserInfo>, CommonRootState, null, Actions> => async dispatch => {
  return new Promise<IUserInfo>(async (resolve, reject) => {
    try {
      dispatch(fetchUserInfoRequest());

      const response = await axios.get<IUserInfo>('/api/v3/userInfo');

      dispatch(fetchUserInfoSuccess(response.data));
      resolve(response.data);
    } catch (error) {
      dispatch(fetchUserInfoFailure(error));
      reject(error);
    }
  });
};

export const fetchUserInfoIfNeeded = (): ThunkAction<Promise<IUserInfo>, CommonRootState, null, Actions> => (
  dispatch,
  getState
) => {
  return new Promise<IUserInfo>(async resolve => {
    if (shouldFetchUserInfo(getState())) {
      const userInfo = await dispatch(fetchUserInfo());
      resolve(userInfo);
    } else {
      resolve();
    }
  });
};

export type Actions =
  | ReturnType<typeof fetchUserInfoRequest>
  | ReturnType<typeof fetchUserInfoSuccess>
  | ReturnType<typeof fetchUserInfoFailure>;
